import {
    all,
    takeLatest,
    put,
    call,
    race,
    takeEvery,
    select,
    join,
    fork,
    delay,
} from 'redux-saga/effects';
import AnalyticsActionTypes from 'yoda-site-components/lib/actionTypes/AnalyticsActionTypes';
import { getProductsPriceByBrowseApi } from 'yoda-interfaces/lib/Product/PricingApi';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import { getCriteoProducts, triggerBeacon } from 'yoda-interfaces/lib/Catalog/CategoriesApi';
import {
    FETCH_DATA_FROM_CRITEO,
    GET_CRITEO_PRODUCTS_ERROR,
    GET_CRITEO_PRODUCTS_SUCCESS,
    MERGE_SPONSORED_AND_ORGANIC_PRODUCTS,
    TRIGGER_CRITEO_BEACON,
} from '../actionTypes/CriteoActionTypes';
import { MERGE_PRODUCTS, GET_PRODUCT_LIST_ERROR } from '../actionTypes/GalleryActionTypes';
import Application from '../helpers/Application';
import { getCriteoPageID, getPageType } from '../utils';

const logger = Application.getLogger('saga');

export const getStoreContext = (state) => state.context;

export function* CriteoSaga(action) {
    try {
        const cretioBeacons = {};
        const storeContext = yield select(getStoreContext);
        let cretioProducts;
        let finalPrices = null;
        let finalProducts = null;
        let onLoadBeacon = null;

        // TODO: criteo timeout is hard coded please move it to settings of site components.
        const timeout = 12000;

        // criteo Call to fetch PPID's. @Has a timeout of 10 sec.
        const criteoResp = yield race({
            items: call(getCriteoProducts, {
                payload: action.payload,
                storeContext,
            }),
            timeout: delay(timeout),
        });

        // Search API Finder call to fetch product data for Criteo PPID's.
        if (criteoResp.items) {
            const pageType = getPageType(window.location.href);
            const origin = getCriteoPageID(
                pageType === 'g' ? 'gallery' : 'search',
                action.payload.deviceType
            );

            // firing the onLoadBeacon call
            onLoadBeacon = (criteoResp.items.data && criteoResp.items.data.OnLoadBeacon) || null;
            onLoadBeacon && (yield call(triggerBeacon, { url: onLoadBeacon }));

            if (criteoResp.items.data && criteoResp.items.data[`${origin}-AboveGridCarousel`]) {
                cretioProducts = criteoResp.items.data[`${origin}-AboveGridCarousel`];
                const productIds = [];
                const organic = action.payload.products || [];
                if (cretioProducts.ProductAd && cretioProducts.ProductAd.length) {
                    cretioProducts.ProductAd.forEach((product) => {
                        if (productIds.length < 12) {
                            const ppid = product.ParentSKU;
                            cretioBeacons[ppid] = product;
                            !organic[ppid] && productIds.push(ppid);
                        }
                    });
                }
                if (productIds.length) {
                    const { enableCouponInfoPrice, enableEligibleCouponInfoPrice } = yield select(
                        selectFeatureFlags
                    );
                    const params = {
                        enableCouponInfoPrice,
                        enableEligibleCouponInfoPrice,
                        productIds,
                    };
                    if (
                        !(storeContext.featureFlags || {}).disableCachedProductSummariesAggregator
                    ) {
                        params.cachedAPI = true;
                    }
                    const productsPriceByBrowseApiTask = yield fork(getProductsPriceByBrowseApi, {
                        ...params,
                    });
                    const result = yield join(productsPriceByBrowseApiTask);
                    finalProducts = result.allProducts;
                    finalPrices = result.prices;
                }
            }

            if (finalProducts && finalProducts.length > 0) {
                yield all([
                    put({
                        type: GET_CRITEO_PRODUCTS_SUCCESS,
                        cretioZone: {
                            products: finalProducts,
                            deviceType: action.payload.deviceType,
                            beacons: cretioBeacons,
                            prices: finalPrices,
                        },
                    }),
                ]);
            } else {
                logger.log('Criteo error | no products ');
                yield put({ type: GET_CRITEO_PRODUCTS_ERROR });
            }
        } else if (criteoResp.timeout) {
            logger.log('Criteo timed out');
            yield put({ type: GET_CRITEO_PRODUCTS_ERROR });
        }
    } catch (error) {
        yield put({ type: GET_CRITEO_PRODUCTS_ERROR });
        logger.log('Criteo Saga error :', error);
        const errorDetails = [{ errorDescription: 'criteo - script error' }];
        yield put({ type: AnalyticsActionTypes.FORM_ERROR, errorDetails });
    }
}

export function* CriteoMergeProductsSaga({ payload: { itemsPerRow } }) {
    try {
        const criteoProducts = yield select((state) => state.criteoProducts);
        const {
            pagination: { hasPrevPage },
        } = yield select((state) => state.pagination);
        if (criteoProducts && !hasPrevPage) {
            yield put({
                type: MERGE_PRODUCTS,
                itemsPerRow,
                criteoProducts,
            });
        }
    } catch (error) {
        yield put({ type: GET_PRODUCT_LIST_ERROR });
    }
}

export function* CriteoBeaconSaga(action) {
    try {
        yield call(triggerBeacon, { url: action.payload.url });
    } catch (error) {
        logger.log('Criteo Beacon Saga trigger error :', error);
    }
}

const watchGalleryCriteoRequest = function* watchGalleryCriteoRequest() {
    try {
        yield takeLatest(FETCH_DATA_FROM_CRITEO, CriteoSaga);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
    }
};
watchGalleryCriteoRequest.sagaName = 'watchGalleryCriteoRequest';

const watchCriteoMergeProductsSage = function* watchCriteoMergeProductsSage() {
    try {
        yield takeEvery(MERGE_SPONSORED_AND_ORGANIC_PRODUCTS, CriteoMergeProductsSaga);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
    }
};
watchCriteoMergeProductsSage.sagaName = 'watchCriteoMergeProductsSage';

const watchCriteoBeacon = function* watchCriteoBeacon() {
    try {
        yield takeEvery(TRIGGER_CRITEO_BEACON, CriteoBeaconSaga);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
    }
};
watchCriteoBeacon.sagaName = 'watchCriteoBeacon';

export default watchGalleryCriteoRequest;
export { watchCriteoMergeProductsSage, watchCriteoBeacon };
